import React from "react";
import { db } from "../../firebase";
import { Button } from "@mantine/core";
import jsPDF from "jspdf";
import EXIF from "exif-js";
import autoTable from "jspdf-autotable";

import { formatDateTime } from "./date-format";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { Download } from "tabler-icons-react";
import { fetchImageDataUrl } from "./hooks/data-functions";
import { formatChecklistForPdf } from "./checklist-utils";

export interface FieldOption {
  value: any;
  label: string;
}

export interface Field {
  key: string;
  label: string;
  type: string;
  includeInPdf?: boolean | null;
  options?: FieldOption[] | null;
  includeMetadataInPdf?: boolean | null;
  optionsSource?: any;
}

export interface SubmissionDataEntry {
  value: any;
  metadata?: any;
}

export interface Submission {
  submissionDate?: any;
  documentNumber?: string;
  submitterName?: string;
  submitterEmail?: string;
  submitterUid?: string;
  data?: Record<string, SubmissionDataEntry>;
}

export interface PDFExportButtonProps {
  companyId?: string | null;
  templateName?: string;
  fields: Field[];
  submission: Submission;
  hideNullishInPdf?: boolean;
  pdfLogoMaxWidth?: number;
  pdfPhotoMaxWidth?: number;
  pdfSignatureMaxWidth?: number;
  variant?: "filled" | "outline" | "light" | "default" | "subtle";
  color?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

// Helper to correct image orientation using EXIF
async function getOrientedImageDataUrl(imgDataUrl: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "Anonymous";
    img.src = imgDataUrl;
    img.onload = function () {
      EXIF.getData(img as any, function (this: any) {
        const orientation = EXIF.getTag(this, "Orientation");
        if (!orientation || orientation === 1) {
          // No rotation needed
          resolve(imgDataUrl);
        } else {
          // Draw to canvas with correct orientation
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(imgDataUrl);
            return;
          }
          // See: https://magnushoff.com/articles/jpeg-orientation/
          // Orientation reference:
          // 1: default
          // 2: flip horizontal
          // 3: rotate 180
          // 4: flip vertical
          // 5: transpose (flip horizontal + rotate 270)
          // 6: rotate 90 CW
          // 7: transverse (flip horizontal + rotate 90 CW)
          // 8: rotate 270 CW
          switch (orientation) {
            case 2: // flip horizontal
              canvas.width = width;
              canvas.height = height;
              ctx.translate(width, 0);
              ctx.scale(-1, 1);
              break;
            case 3: // rotate 180
              canvas.width = width;
              canvas.height = height;
              ctx.translate(width, height);
              ctx.rotate(Math.PI);
              break;
            case 4: // flip vertical
              canvas.width = width;
              canvas.height = height;
              ctx.translate(0, height);
              ctx.scale(1, -1);
              break;
            case 5: // transpose
              canvas.width = height;
              canvas.height = width;
              ctx.rotate(0.5 * Math.PI);
              ctx.scale(1, -1);
              break;
            case 6: // rotate 90 CW (portrait upright)
              canvas.width = height;
              canvas.height = width;
              ctx.translate(height, 0);
              ctx.rotate(0.5 * Math.PI);
              break;
            case 7: // transverse
              canvas.width = height;
              canvas.height = width;
              ctx.translate(height, width);
              ctx.rotate(0.5 * Math.PI);
              ctx.scale(-1, 1);
              break;
            case 8: // rotate 270 CW
              canvas.width = height;
              canvas.height = width;
              ctx.translate(0, width);
              ctx.rotate(-0.5 * Math.PI);
              break;
            default:
              canvas.width = width;
              canvas.height = height;
              break;
          }
          ctx.drawImage(img, 0, 0);
          const mimeType = imgDataUrl.startsWith("data:image/png") ? "image/png" : "image/jpeg";
          resolve(canvas.toDataURL(mimeType));
        }
      });
    };
    img.onerror = function () {
      resolve(imgDataUrl);
    };
  });
}

async function getImageDataUrl(url: string): Promise<string | null> {
  try {
    // Use fetchImageDataUrl from data-functions.js
    return await fetchImageDataUrl(url);
  } catch {
    return null;
  }
}

function getSelectDisplayValue(field: Field, value: any, metadata: any): any {
  if (field.type === "select") {
    // 1. Check metadata label if available
    if (metadata && typeof metadata === "object" && !Array.isArray(metadata) && metadata.label) {
      return metadata.label;
    }

    // 2. Check static options
    if (field.options && value !== undefined) {
      const option = field.options.find((opt) => opt.value === value);
      if (option) {
        return option.label;
      }
    }

    return value;
  }

  if ((field.type === "multiselect" || field.type === "multiSelect") && Array.isArray(value)) {
    return value
      .map((itemValue, index) => {
        const metadataEntry = Array.isArray(metadata) ? metadata[index] : null;
        if (metadataEntry && metadataEntry.label) {
          return metadataEntry.label;
        }

        if (field.options) {
          const option = field.options.find((opt) => opt.value === itemValue);
          if (option) {
            return option.label;
          }
        }

        return itemValue;
      })
      .join(", ");
  }

  return value;
}

export default function PDFExportButton({
  companyId,
  templateName,
  fields,
  submission,
  hideNullishInPdf,
  pdfLogoMaxWidth = 60,
  pdfPhotoMaxWidth = 80,
  pdfSignatureMaxWidth = 120,
  variant = "outline",
  color = "cap-blue",
  size = "sm",
  fullWidth,
  style,
}: PDFExportButtonProps) {
  const [pdfLoading, setPdfLoading] = React.useState(false);

  async function handlePdfExport() {
    setPdfLoading(true);
    try {
      // Fetch company logo
      let logoUrl = "";
      if (companyId) {
        const companyRef = doc(db, "companies", companyId);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          logoUrl = (companySnap.data() as any).logoUrl || "";
        }
      }
      // PDF setup
      const docPdf = new jsPDF();
      let y = 10;
      // Add logo if available
      if (logoUrl) {
        const logoDataUrl = await getImageDataUrl(logoUrl);
        if (logoDataUrl) {
          // Get image dimensions to preserve aspect ratio
          const img = new window.Image();
          img.src = logoDataUrl;
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
          const maxLogoWidth = pdfLogoMaxWidth;
          let logoWidth = img.width;
          let logoHeight = img.height;
          // Scale down by width, preserving aspect ratio.
          if (logoWidth > maxLogoWidth) {
            const scale = maxLogoWidth / logoWidth;
            logoWidth = logoWidth * scale;
            logoHeight = logoHeight * scale;
          }
          docPdf.addImage(logoDataUrl, "PNG", 15, y, logoWidth, logoHeight);
          y += logoHeight + 2.5;
        }
      }
      // Submitter resolution
      let submitterDisplay = "";
      const rawSubmitterName = submission.submitterName?.trim();
      if (rawSubmitterName && rawSubmitterName !== "undefined undefined" && rawSubmitterName !== "null null") {
        submitterDisplay = rawSubmitterName;
      } else if (submission.submitterEmail) {
        submitterDisplay = submission.submitterEmail;
      }

      docPdf.setFontSize(16);
      const titleY = y + 7;
      docPdf.text(templateName || "Submission", 15, titleY);
      docPdf.setFontSize(10);
      docPdf.setTextColor(100);
      const submissionDateStr = submission.submissionDate ? formatDateTime(submission.submissionDate, "datetime") : "";
      const headerLines: string[] = [];
      if (submitterDisplay) {
        headerLines.push(`Submitted by: ${submitterDisplay}`);
      }
      if (submissionDateStr) {
        headerLines.push(`Date: ${submissionDateStr}`);
      }
      if (headerLines.length > 0) {
        docPdf.text(headerLines.join("   |   "), 15, titleY + 6);
      }
      docPdf.setTextColor(0);
      y = titleY + (headerLines.length > 0 ? 12 : 8);

      // Prepare table data (fields + metadata)
      const tableRows: any[][] = [];
      if (submission.documentNumber) {
        tableRows.push(["Document Number", submission.documentNumber]);
      }
      const imageRows: Array<{ label: string; urls: string[]; type: string }> = [];
      for (const field of fields) {
        if (field.includeInPdf === false) continue;
        const fieldData: SubmissionDataEntry = submission.data && submission.data[field.key] ? submission.data[field.key] : { value: undefined };
        let value = fieldData.value;
        if (hideNullishInPdf && (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0))) {
          continue;
        }
        let hasImage = false;
        let imageUrls: string[] = [];
        // Format value for PDF and collect image URLs
        let hideFromTable = false;
        if (field.type === "photo" || field.type === "signature") {
          hideFromTable = true;
          if (value) {
            hasImage = true;
            imageUrls = [value];
          }
        } else if (field.type === "multiPhoto") {
          hideFromTable = true;
          if (Array.isArray(value) && value.length > 0) {
            hasImage = true;
            imageUrls = value;
          }
        } else if (field.type === "gps") {
          if (Array.isArray(value) && value.length === 2) {
            value = `${value[1]},${value[0]}`;
          } else {
            value = "";
          }
        } else if (field.type === "checklist" || field.type === "checklistWithNotes") {
          value = formatChecklistForPdf(value);
        } else if (field.type === "date" || field.type === "time" || field.type === "datetime") {
          value = formatDateTime(value, field.type);
        }

        // For select and multiselect, use labels instead of values
        if (field.type === "select" || field.type === "multiselect" || field.type === "multiSelect") {
          value = getSelectDisplayValue(field, value, fieldData.metadata);
        }

        if (!hideFromTable) {
          tableRows.push([field.label, value]);
        }
        // Add metadata rows
        if (fieldData.metadata && field.includeMetadataInPdf === true) {
          Object.entries(fieldData.metadata).forEach(([metaKey, metaValue]) => {
            if (typeof metaValue === "object" && metaValue !== null) {
              // Only show the 'value' subKey, and use the label from metaValue.label or metaKey
              const displayLabel = (metaValue as any).label || metaKey;
              const valueEntry = Object.entries(metaValue).find(([subKey]) => subKey === "value");
              if (valueEntry) {
                tableRows.push([`${field.label} - ${displayLabel}`, valueEntry[1]]);
              }
            } else {
              tableRows.push([`${field.label} - ${metaKey}`, metaValue]);
            }
          });
        }
        // Queue image rows for later rendering
        if (hasImage && imageUrls.length > 0) {
          imageRows.push({ label: field.label, urls: imageUrls, type: field.type });
        }
      }

      // Add table
      autoTable(docPdf, {
        startY: y,
        body: tableRows,
        theme: "grid",
        styles: { fontSize: 12 },
      });
      y = (docPdf as any).lastAutoTable.finalY + 10;

      // Add images below table, 400px wide, aspect ratio preserved
      for (const imgRow of imageRows) {
        docPdf.setFontSize(12);
        docPdf.text(`${imgRow.label}`, 15, y);
        y += 6;
        // If multiPhoto, place images side by side
        let x = 15;
        let rowHeight = 0;
        const pageWidth = docPdf.internal.pageSize.getWidth();
        for (const url of imgRow.urls) {
          const imgDataUrl = await getImageDataUrl(url);
          if (imgDataUrl) {
            // Correct orientation using EXIF
            const orientedDataUrl = await getOrientedImageDataUrl(imgDataUrl);
            // Get image dimensions
            const img = new window.Image();
            img.src = orientedDataUrl;
            await new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            });
            // PDF max dimensions (in px, then converted to pt)
            const maxPxWidth = imgRow.type === "signature" ? pdfSignatureMaxWidth : pdfPhotoMaxWidth;
            let drawWidth = img.width;
            let drawHeight = img.height;
            // Scale down by width, preserving aspect ratio.
            if (drawWidth > maxPxWidth) {
              const scale = maxPxWidth / drawWidth;
              drawWidth = drawWidth * scale;
              drawHeight = drawHeight * scale;
            }
            // Convert px to pt for jsPDF
            const pdfWidth = drawWidth / 2.83465;
            const pdfHeight = drawHeight / 2.83465;
            // Check for page overflow and add new page if needed
            const pageHeight = docPdf.internal.pageSize.getHeight();
            if (y + pdfHeight + 8 > pageHeight) {
              docPdf.addPage();
              y = 20; // top margin for new page
              x = 15;
              docPdf.setFontSize(12);
            }
            // If image would overflow page width, wrap to next line
            if (x + pdfWidth > pageWidth - 15) {
              x = 15;
              y += rowHeight + 8;
              rowHeight = 0;
              // Check for page overflow again
              if (y + pdfHeight + 8 > pageHeight) {
                docPdf.addPage();
                y = 20;
                docPdf.setFontSize(12);
              }
            }
            let format = "JPEG";
            if (orientedDataUrl.startsWith("data:image/png")) {
              format = "PNG";
            } else if (orientedDataUrl.startsWith("data:image/webp")) {
              format = "WEBP";
            }
            docPdf.addImage(orientedDataUrl, format, x, y, pdfWidth, pdfHeight);
            x += pdfWidth + 8;
            if (pdfHeight > rowHeight) rowHeight = pdfHeight;
          }
        }
        // After row, move y down by rowHeight
        y += rowHeight + 8;
      }

      // Add Logo Watermark to bottom of all pages
      let logoDataUrlWatermark: string | null = null;
      let watermarkImgRatio = 1;
      try {
        const response = await fetch("/captiva-logo.png");
        const blob = await response.blob();
        logoDataUrlWatermark = await new Promise<string>((resolve, reject) => {
          const reader = new window.FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const img = new window.Image();
        img.src = logoDataUrlWatermark;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        if (img.width && img.height) {
          watermarkImgRatio = img.width / img.height;
        }
      } catch (e) {
        console.warn("Failed to load watermark logo from local URL", e);
      }

      const pageCount = docPdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        docPdf.setPage(i);
        const pageWidth = docPdf.internal.pageSize.getWidth();
        const pageHeight = docPdf.internal.pageSize.getHeight();

        docPdf.setFontSize(8);
        docPdf.setTextColor(150);

        const textStr = "Built with ";
        const textWidth = docPdf.getTextWidth(textStr);

        const watermarkHeight = 5;
        const watermarkWidth = watermarkHeight * watermarkImgRatio;

        const totalWidth = textWidth + (logoDataUrlWatermark ? watermarkWidth + 2 : docPdf.getTextWidth("CaptivaData"));
        const startX = (pageWidth / 2) - (totalWidth / 2);
        const yPos = pageHeight - 10;

        docPdf.text(textStr, startX, yPos);

        if (logoDataUrlWatermark) {
          docPdf.addImage(logoDataUrlWatermark, "PNG", startX + textWidth + 2, yPos - 4, watermarkWidth, watermarkHeight, undefined, 'FAST');
        } else {
          docPdf.text("CaptivaData", startX + textWidth, yPos);
        }
      }

      // Filename: template name + submissionDate date
      let dateStr = "";
      if (submission.submissionDate) {
        dateStr = formatDateTime(submission.submissionDate, "date");
      }
      const safeName = (templateName || "submission").replace(/\s+/g, "_");
      docPdf.save(`${safeName}_${dateStr}.pdf`);
    } catch (err: any) {
      alert("PDF export failed: " + err.message);
    }
    setPdfLoading(false);
  }

  return (
    <Button
      size={size}
      color={color}
      variant={variant}
      fullWidth={fullWidth}
      leftIcon={<Download size={size === "md" ? 16 : 14} />}
      style={style ? { ...style } : { float: "right" }}
      loading={pdfLoading}
      onClick={handlePdfExport}
    >
      Export as PDF
    </Button>
  );
}